import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Lock, Save, Ticket, CalendarClock } from 'lucide-react';
import NavigationBar from '../components/NavigationBar';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { Notification } from '../types';

import { formatPhoneNumber, US_DISPLAY_PHONE_PATTERN } from '../utils/phoneFormatUtils';

const BusinessProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { currentBusiness } = useBusiness();
  const navigate = useNavigate();
  const location = useLocation();
  const planPromotionSectionRef = useRef<HTMLDivElement>(null);

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phoneNumber, setPhoneNumber] = useState(
    formatPhoneNumber(String(user?.phoneNumber ?? user?.phone ?? ''))
  );
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Notification state for navbar
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };
  
  // Load unread notifications count for navbar badge
  useEffect(() => {
    const loadUnreadCount = async () => {
      if (!currentBusiness?.id) return;
      try {
        // Make a lightweight call with limit 1 just to get the total count from pagination
        const response = await apiService.getNotifications(currentBusiness.id, { page: 1, limit: 1, isRead: false });
        // Use pagination.total for the actual count, not the array length
        const totalUnread = response.pagination?.total || 0;
        // Set a dummy array with the count for compatibility, but we'll use totalUnread for display
        setUnreadNotifications(Array(totalUnread).fill(null) as Notification[]);
      } catch (error) {
        console.error('Error loading unread notifications count:', error);
        setUnreadNotifications([]);
      }
    };
    loadUnreadCount();
  }, [currentBusiness?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isSidebarOpen) {
        const target = event.target as HTMLElement;
        if (!target.closest('.sidebar-container')) {
          setIsSidebarOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSidebarOpen]);

  // Auto-scroll to Plan & Promotion when opened via #plan-promotion (e.g. expired account dialog)
  useEffect(() => {
    if (location.hash !== '#plan-promotion') return;
    const timer = window.setTimeout(() => {
      planPromotionSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedPhone = phoneNumber.trim();
    if (trimmedPhone && !US_DISPLAY_PHONE_PATTERN.test(trimmedPhone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setSaving(true);
    try {
      const updated = await apiService.updateCurrentUser({
        firstName,
        lastName,
        email,
        phoneNumber: trimmedPhone,
        phone: trimmedPhone,
      });
      // Preserve roles and other important fields that might not be returned by the API
      const updatedUserWithRoles = {
        ...updated,
        roles: updated.roles || user?.roles || [],
        canPerformServices: updated.canPerformServices ?? user?.canPerformServices
      };
      updateUser(updatedUserWithRoles);
      toast.success('Profile updated successfully');
      // Redirect to business dashboard after successful save
      navigate('/dashboard/business');
    } catch (err: any) {
      console.error('Failed to update profile', err);
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to update profile';
      toast.error(typeof msg === 'string' ? msg : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const formatUtcDateTime = (value: any): string => {
    if (!value) return 'Unknown';
    const raw = String(value);
    // Be tolerant of common Postgres-ish formats like "2026-06-30 12:59:59+00"
    const normalized =
      /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(raw) ? raw.replace(' ', 'T') : raw;
    const d = value instanceof Date ? value : new Date(normalized);
    if (Number.isNaN(d.getTime())) return 'Unknown';
    return d.toLocaleString(undefined, { timeZone: 'UTC' });
  };

  const businessValidUntil = formatUtcDateTime((currentBusiness as any)?.validUntil);
  const businessPromoCode = ((currentBusiness as any)?.referralCode || '').toString().trim();

  // Handle notification bell click
  const handleNotificationsClick = () => {
    navigate('/dashboard/business?section=notifications');
  };

  return (
    <div className="min-h-screen bg-gray-50 no-animations">
      <NavigationBar 
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={handleNotificationsClick}
      />
      
      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr] overflow-x-hidden">
        <BusinessDashboardSidebar
          isSidebarOpen={isSidebarOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          // No matching dashboard section on this standalone page; omit activeSection highlighting.
          onClose={() => setIsSidebarOpen(false)}
          onToggleCollapse={toggleSidebar}
          onSectionChange={(section) => navigate(`/dashboard/business?section=${section}`)}
          unreadNotificationCount={unreadNotifications.length}
        />

        {/* Main Content */}
        <div className="flex flex-col flex-1 overflow-x-hidden">
          {/* Mobile Header */}
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="flex-1 text-center text-lg font-semibold text-gray-900">Profile Settings</h1>
              {/* Invisible spacer to balance hamburger button and center the title */}
              <div className="w-10 h-10"></div>
            </div>
          </div>
          
          <div className="p-4 md:p-8">
            {/* Header */}
            <div className="mb-8">
              {/* Desktop Header */}
              <div className="hidden md:block">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Profile Settings</h2>
                <p className="text-gray-600 mt-1">Manage your personal information and account security</p>
              </div>
              {/* Mobile Description */}
              <div className="md:hidden">
                <p className="text-gray-600 text-sm">Manage your personal information and account security</p>
              </div>
            </div>

          {/* Main Content */}
          <div className="space-y-6">
            {/* Personal Information Section */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
                    <p className="text-sm text-gray-500">Update your personal details and contact information</p>
                  </div>
                </div>
              </div>
              
              <form ref={formRef} onSubmit={handleSave} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                  <input
                      id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                      placeholder="Enter your first name"
                    required
                  />
                  </div>
                  
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                  <input
                      id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                      placeholder="Enter your last name"
                    required
                  />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                  <input
                        id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                        placeholder="your.email@example.com"
                    required
                  />
                    </div>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                      <span className="ml-2 text-xs font-normal text-gray-500">(Optional)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                  <input
                        id="phoneNumber"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                    placeholder="(555) 123-4567"
                  />
                  </div>
                  </div>
              </div>
                
                <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end">
                <button
                  type="submit"
                  onClick={() => formRef.current?.requestSubmit()}
                  disabled={saving}
                  className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                </button>
                </div>
              </form>
            </div>

            {/* Plan & Promotion Section */}
            <div
              id="plan-promotion"
              ref={planPromotionSectionRef}
              className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden scroll-mt-24"
            >
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center">
                  <div className="p-2 bg-emerald-100 rounded-lg mr-3">
                    <CalendarClock className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Plan & Promotion</h2>
                    <p className="text-sm text-gray-500">View your current access expiration and any promo code applied</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <CalendarClock className="w-4 h-4 mr-2 text-gray-500" />
                      Expiration (valid until)
                    </div>
                    <div className="text-gray-900 font-semibold">{businessValidUntil}</div>
                  </div>

                  <div>
                    <div className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <Ticket className="w-4 h-4 mr-2 text-gray-500" />
                      Promotion code
                    </div>
                    <div className="text-gray-900 font-semibold">{businessPromoCode ? businessPromoCode : 'None'}</div>
                    <p className="mt-1 text-xs text-gray-500">
                      The promo code is captured at registration time.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Section */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg mr-3">
                    <Lock className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Security</h2>
                    <p className="text-sm text-gray-500">Manage your password and account security</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="flex items-center justify-between py-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">Password</h3>
                    <p className="text-sm text-gray-500 mt-1">Last updated: {user?.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : 'Never'}</p>
                  </div>
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(true)}
                    className="ml-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    <Lock className="w-4 h-4 mr-2" />
                  Change Password
                </button>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg mr-3">
                    <Lock className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
                </div>
                <button 
                  onClick={() => setShowPasswordModal(false)} 
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!currentPassword || !newPassword) {
                  toast.error('Please fill out all fields');
                  return;
                }
                if (newPassword !== confirmPassword) {
                  toast.error('New passwords do not match');
                  return;
                }
                setChangingPassword(true);
                try {
                  await apiService.changePassword(currentPassword, newPassword);
                  toast.success('Password changed successfully');
                  setShowPasswordModal(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                } catch (err: any) {
                  console.error('Change password error', err);
                  toast.error(err?.response?.data?.message || 'Failed to change password');
                } finally {
                  setChangingPassword(false);
                }
              }}
              className="p-6 space-y-5"
            >
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password <span className="text-red-500">*</span>
                </label>
                <input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                  placeholder="Enter your current password"
                  required
                />
              </div>
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password <span className="text-red-500">*</span>
                </label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                  placeholder="Enter your new password"
                  required
                  minLength={8}
                />
                <p className="mt-2 text-xs text-gray-500">
                  Password must be at least 8 characters and include uppercase, lowercase, and a number.
                </p>
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password <span className="text-red-500">*</span>
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                  placeholder="Confirm your new password"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="aacore-btn2-outline disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={changingPassword}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {changingPassword ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Changing...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessProfilePage;

